import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, {
  APPROVAL_EVENTS,
  CLOSED_PERIOD_POLICIES,
  POSTING_GRANULARITIES,
  POSTING_MODES,
  RECOGNITION_JOBS,
  RECOGNITION_METHODS,
  type JobRun,
  type RecognitionCategory,
  type RecognitionPolicy,
} from '@/services/financeRecognition.service';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { METHOD_LABEL, POSTING_MODE_LABEL, StatusTag, accountLabel, apiErrorMessage, asObject, humanize, options, useRecognitionAccess } from './prepaidDisplay';

const { Title, Text } = Typography;

const JOB_HELP: Record<string, string> = {
  UPDATE_DUE_LINES: 'Marks planned schedule lines whose recognition date has arrived as due.',
  GENERATE_DRAFT_JVS: 'Creates draft journals for due lines on contracts using auto-create-draft posting.',
  AUTO_POST: 'Posts due lines on contracts using auto-post (only when the policy allows auto-posting).',
  EXPIRY_NOTIFICATIONS: 'Notifies owners about contracts expiring within the policy notice window.',
  INTEGRITY_CHECK: 'Verifies subledger invariants and notifies finance about inconsistencies.',
};

const CLOSED_POLICY_LABEL: Record<string, string> = {
  HOLD: 'Hold — line is blocked until finance resolves it',
  NEXT_OPEN_PERIOD: 'Post to the next open period automatically',
};

const toArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string' && v.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const truthy = (v: unknown) => v === true || Number(v) === 1;

function useAccountOptions() {
  const { data: asset = [] } = useChartAccountsByType('Asset', false);
  const { data: expense = [] } = useChartAccountsByType('Expense', false);
  return useMemo(() => {
    const toOpt = (a: { id: string; account_code?: string; account_name?: string }) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) });
    const label = new Map<string, string>();
    [...asset, ...expense].forEach((a) => label.set(a.id, accountLabel(a.account_code, a.account_name)));
    return { asset: asset.map(toOpt), expense: expense.map(toOpt), label };
  }, [asset, expense]);
}

const PolicyTab: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const accounts = useAccountOptions();
  const { data: policy, isLoading } = useQuery({ queryKey: ['recognition', 'policy'], queryFn: () => recognitionService.getPolicy() });

  useEffect(() => {
    if (!policy) return;
    form.setFieldsValue({
      ...policy,
      allow_auto_post: truthy(policy.allow_auto_post),
      allow_re_recognition: truthy(policy.allow_re_recognition),
      approval_required_events: toArray(policy.approval_required_events),
      rounding_tolerance: Number(policy.rounding_tolerance ?? 0),
    });
  }, [policy, form]);

  const save = useMutation({
    mutationFn: (v: Partial<RecognitionPolicy>) =>
      recognitionService.updatePolicy({
        ...v,
        refund_receivable_account_id: v.refund_receivable_account_id ?? null,
        write_off_account_id: v.write_off_account_id ?? null,
        rounding_tolerance: v.rounding_tolerance !== undefined && v.rounding_tolerance !== null ? String(v.rounding_tolerance) : undefined,
      }),
    onSuccess: () => {
      message.success(t('recognitionPolicySaved'));
      qc.invalidateQueries({ queryKey: ['recognition', 'policy'] });
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  return (
    <Card size="small" loading={isLoading}>
      <Form form={form} layout="vertical" style={{ maxWidth: 720 }} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="default_posting_mode" label={t('defaultPostingMode')} rules={[{ required: true }]}>
          <Select options={options(POSTING_MODES, POSTING_MODE_LABEL)} />
        </Form.Item>
        <Form.Item name="allow_auto_post" label={t('allowAutoPosting')} valuePropName="checked" extra="When off, contracts cannot use auto-post and the auto-post job posts nothing.">
          <Switch />
        </Form.Item>
        <Form.Item name="posting_granularity" label={t('journalGranularity')} rules={[{ required: true }]} extra="How recognition runs group due lines into journals.">
          <Select options={options(POSTING_GRANULARITIES)} />
        </Form.Item>
        <Form.Item name="closed_period_policy" label={t('closedPeriodHandling')} rules={[{ required: true }]}>
          <Select options={options(CLOSED_PERIOD_POLICIES, CLOSED_POLICY_LABEL)} />
        </Form.Item>
        <Form.Item name="allow_re_recognition" label={t('allowReRecognitionReversed')} valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="refund_receivable_account_id" label={t('defaultRefundReceivableAccount')}>
          <Select allowClear showSearch optionFilterProp="label" options={accounts.asset} placeholder={t('assetAccount')} />
        </Form.Item>
        <Form.Item name="write_off_account_id" label={t('defaultWriteOffAccount')}>
          <Select allowClear showSearch optionFilterProp="label" options={accounts.expense} placeholder={t('expenseAccount')} />
        </Form.Item>
        <Form.Item name="approval_required_events" label={t('eventsRequiringApproval')} extra="These events route through the Approval Runtime before they are applied.">
          <Select mode="multiple" allowClear options={options(APPROVAL_EVENTS)} />
        </Form.Item>
        <Space size={24}>
          <Form.Item name="rounding_tolerance" label={t('roundingTolerance')} rules={[{ required: true }]}>
            <InputNumber min={0} max={1} step={0.01} precision={4} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="expiry_notice_days" label={t('expiryNoticeDays')} rules={[{ required: true }]}>
            <InputNumber min={0} max={365} precision={0} style={{ width: 160 }} />
          </Form.Item>
        </Space>
        {policy?.updated_at && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Last updated {dayjs(policy.updated_at).format('DD MMM YYYY HH:mm')}
          </Text>
        )}
        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={save.isPending}>
          {t('savePolicy')}
        </Button>
      </Form>
    </Card>
  );
};

const CategoriesTab: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const accounts = useAccountOptions();
  const [editing, setEditing] = useState<RecognitionCategory | 'new' | null>(null);
  const { data = [], isLoading } = useQuery({ queryKey: ['recognition', 'categories', 'all'], queryFn: () => recognitionService.listCategories(true) });

  const save = useMutation({
    mutationFn: (v: Partial<RecognitionCategory>) =>
      editing && editing !== 'new' ? recognitionService.updateCategory(editing.id, { ...v, code: undefined }) : recognitionService.createCategory(v),
    onSuccess: () => {
      message.success(t('categorySaved'));
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['recognition', 'categories'] });
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const open = (row: RecognitionCategory | 'new') => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue(row === 'new' ? { is_active: true, default_recognition_method: 'DAILY_PRO_RATA', default_posting_mode: 'AUTO_CREATE_DRAFT' } : { ...row, is_active: truthy(row.is_active) });
  };

  return (
    <Card
      size="small"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
          {t('newCategory')}
        </Button>
      }
    >
      <Table<RecognitionCategory>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        pagination={false}
        onRow={(r) => ({ onClick: () => open(r), style: { cursor: 'pointer' } })}
        columns={[
          { title: t('code'), dataIndex: 'code', width: 200 },
          { title: t('name'), dataIndex: 'name' },
          { title: t('defaultPrepaidAccount'), dataIndex: 'default_prepaid_asset_account_id', render: (v: string | null) => (v ? accounts.label.get(v) ?? '—' : '—') },
          { title: t('defaultExpenseAccount'), dataIndex: 'default_expense_account_id', render: (v: string | null) => (v ? accounts.label.get(v) ?? '—' : '—') },
          { title: t('method'), dataIndex: 'default_recognition_method', render: (v: string | null) => (v ? METHOD_LABEL[v] ?? humanize(v) : '—') },
          { title: t('posting'), dataIndex: 'default_posting_mode', render: (v: string | null) => (v ? POSTING_MODE_LABEL[v] ?? humanize(v) : '—') },
          { title: t('active'), dataIndex: 'is_active', width: 80, render: (v: unknown) => (truthy(v) ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
        ]}
      />
      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'New prepaid category' : 'Edit prepaid category'}
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        okText={tc('save')}
        confirmLoading={save.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true, message: t('codeRequired') }]} extra={t('codeFormatHint')}>
            <Input disabled={editing !== 'new'} maxLength={50} />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true, message: t('nameRequired') }]}>
            <Input maxLength={150} />
          </Form.Item>
          <Form.Item name="default_prepaid_asset_account_id" label={t('defaultPrepaidAssetAccount')}>
            <Select allowClear showSearch optionFilterProp="label" options={accounts.asset} />
          </Form.Item>
          <Form.Item name="default_expense_account_id" label={t('defaultExpenseAccount')}>
            <Select allowClear showSearch optionFilterProp="label" options={accounts.expense} />
          </Form.Item>
          <Form.Item name="default_recognition_method" label={t('defaultRecognitionMethod')}>
            <Select allowClear options={options(RECOGNITION_METHODS, METHOD_LABEL)} />
          </Form.Item>
          <Form.Item name="default_posting_mode" label={t('defaultPostingMode')}>
            <Select allowClear options={options(POSTING_MODES, POSTING_MODE_LABEL)} />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

const ControlledAccountsTab: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { message } = App.useApp();
  const qc = useQueryClient();
  const accounts = useAccountOptions();
  const [toAdd, setToAdd] = useState<string>();
  const { data = [], isLoading } = useQuery({ queryKey: ['recognition', 'controlled-accounts'], queryFn: () => recognitionService.listControlledAccounts() });

  const setControlled = useMutation({
    mutationFn: ({ id, controlled }: { id: string; controlled: boolean }) => recognitionService.setControlledAccount(id, controlled),
    onSuccess: (_r, v) => {
      message.success(v.controlled ? 'Account is now recognition-controlled' : 'Account control removed');
      setToAdd(undefined);
      qc.invalidateQueries({ queryKey: ['recognition', 'controlled-accounts'] });
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  const controlledIds = new Set(data.map((a) => a.id));
  return (
    <Card size="small">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t('recognitionControlledAccountsHint')}
      />
      <Space style={{ marginBottom: 12 }}>
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder={t('assetAccountToControl')}
          style={{ width: 360 }}
          value={toAdd}
          onChange={setToAdd}
          options={accounts.asset.filter((o) => !controlledIds.has(o.value))}
        />
        <Button type="primary" disabled={!toAdd} loading={setControlled.isPending} onClick={() => toAdd && setControlled.mutate({ id: toAdd, controlled: true })}>
          {t('addControl')}
        </Button>
      </Space>
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        pagination={false}
        columns={[
          { title: t('account'), key: 'a', render: (_: unknown, r) => accountLabel(r.account_code, r.account_name) },
          { title: t('type'), dataIndex: 'account_type', width: 120 },
          { title: t('active'), dataIndex: 'is_active', width: 80, render: (v: unknown) => (truthy(v) ? 'Yes' : 'No') },
          {
            title: '',
            key: 'x',
            width: 140,
            render: (_: unknown, r) => (
              <Popconfirm title={t('removeRecognitionControlConfirm')} onConfirm={() => setControlled.mutate({ id: r.id, controlled: false })}>
                <Button size="small" danger>
                  {t('removeControl')}
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />
    </Card>
  );
};

const JobsTab: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { data = [], isLoading, refetch, isFetching } = useQuery({ queryKey: ['recognition', 'job-runs'], queryFn: () => recognitionService.jobRuns(50) });
  const run = useMutation({
    mutationFn: (job: string) => recognitionService.runJob(job),
    onSuccess: (r) => {
      message.success(`${humanize(r.job)}: ${r.processed} processed, ${r.failed} failed`);
      qc.invalidateQueries({ queryKey: ['recognition', 'job-runs'] });
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card size="small" title={t('runJobNow')}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {RECOGNITION_JOBS.map((j) => (
            <Space key={j} style={{ width: '100%', justifyContent: 'space-between' }}>
              <span>
                <Text strong>{humanize(j)}</Text> <Text type="secondary">— {JOB_HELP[j]}</Text>
              </span>
              <Popconfirm title={`Run ${humanize(j)} now?`} onConfirm={() => run.mutate(j)}>
                <Button size="small" icon={<PlayCircleOutlined />} loading={run.isPending && run.variables === j}>
                  {t('run')}
                </Button>
              </Popconfirm>
            </Space>
          ))}
        </Space>
      </Card>
      <Card
        size="small"
        title={t('recentJobRuns')}
        extra={
          <Button size="small" icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            {tc('refresh')}
          </Button>
        }
      >
        <Table<JobRun>
          size="small"
          rowKey="id"
          loading={isLoading}
          dataSource={data}
          pagination={{ pageSize: 20 }}
          expandable={{
            rowExpandable: (r) => Boolean(r.error_message || r.summary),
            expandedRowRender: (r) => (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{r.error_message ? r.error_message : JSON.stringify(asObject(r.summary), null, 2)}</pre>
            ),
          }}
          columns={[
            { title: t('job'), dataIndex: 'job_name', render: (v: string) => humanize(v) },
            { title: tc('status'), dataIndex: 'status', width: 120, render: (v: string) => <StatusTag status={v} /> },
            { title: t('processed'), dataIndex: 'processed_count', width: 100, align: 'right' },
            { title: t('failed'), dataIndex: 'failed_count', width: 90, align: 'right' },
            { title: t('started'), dataIndex: 'started_at', width: 170, render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY HH:mm:ss') : '—') },
            { title: t('finished'), dataIndex: 'finished_at', width: 170, render: (v: string | null) => (v ? dayjs(v).format('DD MMM YYYY HH:mm:ss') : '—') },
          ]}
        />
      </Card>
    </Space>
  );
};

const PrepaidSettingsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const access = useRecognitionAccess();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'policy';

  if (!access.canAdmin) return <Alert type="warning" showIcon style={{ margin: 24 }} message={t('needPermissionAdminSettings')} />;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid')}>
          {t('workspace')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {t('prepaidExpenseSettings')}
        </Title>
      </Space>
      <Tabs
        activeKey={tab}
        onChange={(k) => setParams({ tab: k }, { replace: true })}
        items={[
          { key: 'policy', label: t('policy'), children: <PolicyTab /> },
          { key: 'categories', label: t('categories'), children: <CategoriesTab /> },
          { key: 'controlled', label: t('controlledAccounts'), children: <ControlledAccountsTab /> },
          { key: 'jobs', label: t('jobs'), children: <JobsTab /> },
        ]}
      />
    </div>
  );
};

export default PrepaidSettingsPage;
